import { z } from 'zod';
import type {
  ConversationItemResponse,
  HubItemResponse,
  MessageItemResponse,
  AvailableTaskHub,
} from './chat';
import type { NavigationCounts } from './live-updates';
import type { UserDataResponse } from './users';
import type { HubAlert } from './hub-alerts';
import type { PlatformNotice } from './notices';
import type { PeopleSessionSnapshot } from './people-threads';

// ---------------------------------------------------------------------------
// GET /users/me/bootstrap — query
// ---------------------------------------------------------------------------

/**
 * Bootstrap query — optional active URL hints resolved by the RSC.
 *
 * Route-level RSC parses the current URL (e.g. /chat/[hubId]/[conversationId])
 * and passes hints to the backend so the snapshot can pre-load the active
 * conversation's first-page messages.
 */
export const bootstrapQuery = z.object({
  active_hub_id: z.string().uuid().optional(),
  active_conversation_id: z.string().uuid().optional(),
  active_nav: z.enum(['chat', 'task', 'support']).optional(),
  /**
   * The people thread the URL names (`/chat/threads/:threadId`, gh #4661): the snapshot
   * preloads it as `people.active_thread` when the viewer is a member.
   */
  active_thread_id: z.string().uuid().optional(),
});

export type BootstrapQuery = z.infer<typeof bootstrapQuery>;

// ---------------------------------------------------------------------------
// WebSocket — request_snapshot_chunk (cursor-paged follow-up)
// ---------------------------------------------------------------------------

export const requestSnapshotChunkMessage = z.object({
  type: z.literal('request_snapshot_chunk'),
  cursor: z.string(),
});

export type RequestSnapshotChunkMessage = z.infer<typeof requestSnapshotChunkMessage>;

// ---------------------------------------------------------------------------
// Response shape — shared between HTTP (/bootstrap) and WS (session_snapshot)
// ---------------------------------------------------------------------------

/**
 * Per-nav conversation slice. Arrays so the RSC-serialized payload crosses
 * the Next.js boundary without Map serialization issues; client hydrator
 * feeds these into Zustand Map setters.
 */
export interface SessionSnapshotConversations {
  chat: ConversationItemResponse[];
  task: ConversationItemResponse[];
  support: ConversationItemResponse[];
}

/**
 * Active conversation pre-loaded by the RSC. Present only when the URL
 * resolved to a specific conversation the user has access to.
 */
export interface SessionSnapshotActiveConversation {
  conversation: ConversationItemResponse;
  messages: MessageItemResponse[];
}

/**
 * Per-user session snapshot. Built once in
 * `workers/backend/src/live-updates/session-snapshot.ts` and consumed by both:
 * - HTTP: `GET /api/users/me/bootstrap` (RSC first paint)
 * - WS:   `LiveUpdateSession.getSessionSnapshot()` RPC (on auth_ok)
 */
export interface SessionSnapshot {
  user: UserDataResponse;
  hubs: HubItemResponse[];
  available_task_hubs: AvailableTaskHub[];
  conversations: SessionSnapshotConversations;
  active_conversation: SessionSnapshotActiveConversation | null;
  nav_counts: NavigationCounts;
  /**
   * Maps hub_id → the viewer's hub-scoped user_id (PK of HubDO's local `user`
   * table) for each hub where the viewer is an end user. Optional: a Worker
   * that predates this field omits it. Hubs with no resolvable
   * `hub_user.user_id` are absent from the map.
   */
  hub_user_ids_by_hub?: Record<string, string>;
  /**
   * Hub ids where the viewer has team/admin (support) access — i.e. the
   * `teamHubIds` set the builder uses for the support scope. Drives the live
   * update path's per-recipient `source_navs` inference (a recipient adds
   * `'support'` membership for a conversation whose hub is in this set).
   * Optional: a Worker that predates this field omits it.
   */
  support_hub_ids?: string[];
  /**
   * Active Status & Notices hub-health alerts across the hubs the viewer admins
   * (hub-local + org-derived union, body-free, each stamped with `hub_id`). Seeds the
   * app-shell banner + the cross-hub aggregate without a separate fetch — "always
   * live". Optional: a Worker that predates this field omits it.
   *
   * NOTE: a shared org credential backing M connections in one hub yields M entries
   * with the SAME `alert_id` (distinct `connection_id`) — the fan-out that surfaces the
   * one org alert against each linking connection. Consumers MUST key by the composite
   * `${alert_id}:${connection_id}` (not `alert_id` alone), as the Status tab does.
   */
  active_alerts?: HubAlert[];
  /**
   * Active platform broadcasts (Status & Notices feed 2) visible to this viewer —
   * `global`-scoped, within their display window, minus the ones they've acknowledged.
   * The same set `GET /api/notices` returns; seeds the notice banner + the account
   * aggregate without a separate fetch ("always live"). Optional: a Worker that
   * predates this field omits it. Consumers key by `notice_id`.
   */
  active_notices?: PlatformNotice[];
  /**
   * Authoritative per-hub unread badge state the builder computed for this viewer,
   * used by the two BACKEND consumers (the `/bootstrap` route + the WS cold-start)
   * to write back the LiveUpdateSession nav counters WITHOUT re-deriving from the
   * shipped conversation slices. Since Phase 1 (bootstrap-snapshot-diet) ships only
   * a HEAD (~5) of each non-active hub's list, re-deriving counts from the slices
   * would undercount; this field carries the exact counts (support badge computed
   * from the full HubDO index, chat/task from the per-hub own-unread aggregate).
   *
   * Backend-only: the frontend hydrator ignores it (badges arrive live via the WS
   * nav_counts frame). Optional: a Worker that predates the diet omits it, and both
   * consumers fall back to re-deriving from the slices (correct when lists are full).
   */
  nav_count_state?: {
    chatByHub: Record<string, number>;
    taskByHub: Record<string, number>;
    supportByHub: Record<string, number>;
    /**
     * People threads with unread messages (gh #4661), from the viewer's UserDO. Absent while
     * the surface is off (or for a caller that is not an interactive session); null when the
     * people read or the switch read failed, which tells the writer to keep the badge it
     * already holds.
     */
    threads?: number | null;
  };
  /**
   * People threads and contact invites (gh #4661): the thread list's head, the pending
   * incoming invite count and the thread the `active_thread_id` hint names. Present only
   * while the threads surface is on (`platform_config.threads_enabled`) and only for an
   * interactive sign-in session. Optional: a Worker that predates it, a surface that is off,
   * or a people read that failed omits it (`GET /api/threads` serves the same list).
   */
  people?: PeopleSessionSnapshot;
  /**
   * Serialized size ceiling guard — when the full payload exceeds the
   * snapshot size budget, the builder paginates via cursor. `null` = done.
   */
  cursor: string | null;
}

export interface BootstrapResponse {
  success: true;
  data: SessionSnapshot;
}

// ---------------------------------------------------------------------------
// WebSocket — session_snapshot message (emitted after auth_ok)
// ---------------------------------------------------------------------------

export interface SessionSnapshotMessage {
  type: 'session_snapshot';
  payload: SessionSnapshot;
}
